import React, { useState, useEffect, memo, useRef } from "react";
import apiRequest from "../../services/apiClient";
import { isSuccessStatusCode, labelTextSecretKey, labelTextSecretValue } from "../../utils/utils";
import { useSnackbar } from 'notistack';
import SecretTypeSelect from "../../components/secretTypeSelect.component";
import CardTypeSelect from "../../components/cardTypeSelect.component";
import CurrencySelect from "../../components/currencySelect.component";
import AutocompleteServerList, { invalidateCache } from "../../components/serverAutocomplete.component";
import { secretTypesConstants } from "../../utils/enums";

// Manager para el singleton del formulario de secret
class SecretFormManager {
  constructor() {
    this.item = {};
    this.loading = false;
    this.submitting = false;
    this.errors = {};
    this.listeners = new Set();
    this.currentRequest = null;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.getState());
  }

  unsubscribe(listener) {
    this.listeners.delete(listener);
  }

  getState() {
    return {
      item: this.item,
      loading: this.loading,
      submitting: this.submitting,
      errors: this.errors,
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.getState()));
  }

  async loadSecret(editingItem) {
    if (this.loading) {
      console.log('Already loading form data, ignoring request for:', editingItem);
      return;
    }

    this.loading = true;
    this.notifyListeners();

    // Cancelar petición anterior si existe
    if (this.currentRequest) {
      this.currentRequest.cancel?.();
    }

    const request = apiRequest(`secretItem/${editingItem}?$expand=section($select=id,name),client($select=id,name)`);
    this.currentRequest = request;

    try {
      const response = await request;
      if (this.currentRequest === request) {
        let editingItemData = {
          ...response.data,
          secretKey: '',
          secretValue: ''
        };

        // Cargar secretKey si existe
        if (response.data.secretKey && response.data.secretKey.length > 0) {
          try {
            const secretKeyResponse = await apiRequest(`secretItem/showSecretKey?secretId=${editingItem}`);
            editingItemData['secretKey'] = secretKeyResponse.data.secretKey;
          } catch (error) {
            console.error('Error loading secret key:', error);
          }
        }

        // Cargar secretValue si existe
        if (response.data.secretValue && response.data.secretValue.length > 0) {
          try {
            const secretValueResponse = await apiRequest(`secretItem/showSecretValue?secretId=${editingItem}`);
            editingItemData['secretValue'] = secretValueResponse.data.secretValue;
          } catch (error) {
            console.error('Error loading secret value:', error);
          }
        }

        this.item = editingItemData;
        this.errors = {};
      }
    } catch (error) {
      if (this.currentRequest === request) {
        console.error('Error loading secret for form:', error);
      }
    } finally {
      if (this.currentRequest === request) {
        this.loading = false;
        this.currentRequest = null;
        this.notifyListeners();
      }
    }
  }

  async handleSubmit(item, editingItem, onSecretAdd, enqueueSnackbar) {
    this.submitting = true;
    this.errors = {};
    this.notifyListeners();

    try {
      const response = await apiRequest("secretItem", {
        method: !editingItem ? "POST" : "PUT",
        data: item
      });

      if (isSuccessStatusCode(response.status)) {
        onSecretAdd();
        this.item = {}; // Limpiar formulario
        
        // Invalidar caché de secrets para refrescar la lista
        invalidateCache('secretItem');
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message ?? "An unknown error occurred", { variant: 'error' });
      try {
        const validationErrors = error.response?.data?.validationErrors;
        if (validationErrors instanceof Array) {
          const newErrors = {};
          validationErrors.forEach(element => {
            newErrors[element.field] = element.errorMessages.join(' ');
          });
          this.errors = newErrors;
        }
      } catch (e) {
        // ignore
      }
    } finally {
      this.submitting = false;
      this.notifyListeners();
    }
  }

  setItemField(name, value) {
    this.item = { ...this.item, [name]: value };
    this.notifyListeners();
  }

  resetForm() {
    this.item = {};
    this.errors = {};
    this.loading = false;
    this.submitting = false;
    this.currentRequest = null;
    this.notifyListeners();
  }
}

const secretFormManager = new SecretFormManager();

const SecretFormSingleton = memo(({ editingItem, closeForm, onSecretAdd }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = useState(secretFormManager.getState());

  const updateFormState = (newState) => {
    setFormState(newState);
  };

  useEffect(() => {
    secretFormManager.subscribe(updateFormState);
    return () => secretFormManager.unsubscribe(updateFormState);
  }, []);

  useEffect(() => {
    if (editingItem) {
      secretFormManager.loadSecret(editingItem);
    } else {
      secretFormManager.resetForm();
    }
  }, [editingItem]);

  const handleSubmit = (event) => {
    event.preventDefault();
    secretFormManager.handleSubmit(formState.item, editingItem, onSecretAdd, enqueueSnackbar);
  };

  const handleItemChange = (e) => {
    secretFormManager.setItemField(e.target.name, e.target.value);
  };

  const { item, loading, submitting, errors } = formState;

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <SecretTypeSelect
              value={item?.type}
              onChange={(value) => {
                secretFormManager.setItemField('type', parseInt(value));
              }}
            />
            {errors["type"] && (
              <span className="text-red-500 text-sm">{errors["type"]}</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
            <AutocompleteServerList
              entityName="section"
              selectQuery="?$select=id,name"
              value={item?.sectionId}
              onChange={(value) => {
                secretFormManager.setItemField('sectionId', value);
              }}
              placeholder="Select section..."
            />
            {errors["sectionId"] && (
              <span className="text-red-500 text-sm">{errors["sectionId"]}</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <AutocompleteServerList
              entityName="client"
              selectQuery="?$select=id,name"
              value={item?.clientId}
              onChange={(value) => {
                secretFormManager.setItemField('clientId', value);
              }}
              placeholder="Select client..."
            />
            {errors["clientId"] && (
              <span className="text-red-500 text-sm">{errors["clientId"]}</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={item?.name || ''}
              onChange={handleItemChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors["name"] ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors["name"] && (
              <span className="text-red-500 text-sm">{errors["name"]}</span>
            )}
          </div>

          {item?.type !== secretTypesConstants.CreditCards && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {(item?.type === secretTypesConstants.BankAccounts || item?.type === secretTypesConstants.BankDetractionAccounts) ? "Bank Url" : "Host"}
              </label>
              <input
                type="text"
                name="host"
                value={item?.host || ''}
                onChange={handleItemChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors["host"] ? 'border-red-500' : 'border-gray-300'
                }`}
                required={item?.type === secretTypesConstants.DatabaseCredentials}
              />
              {errors["host"] && (
                <span className="text-red-500 text-sm">{errors["host"]}</span>
              )}
            </div>
          )}

          {(item?.type === secretTypesConstants.CreditCards ||
            item?.type === secretTypesConstants.BankAccounts ||
            item?.type === secretTypesConstants.BankDetractionAccounts) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <CurrencySelect
                label="Currency"
                selectId="currency"
                defaultSelectedItem={item?.currency}
                setSelectedItem={(selected) => {
                  secretFormManager.setItemField('currency', selected?.value);
                }}
                showLabel={false}
              />
              {errors["currency"] && (
                <span className="text-red-500 text-sm">{errors["currency"]}</span>
              )}
            </div>
          )}

          {(item?.type === secretTypesConstants.BankAccounts ||
            item?.type === secretTypesConstants.BankDetractionAccounts) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
              <input
                type="text"
                name="accountNumber"
                value={item?.accountNumber || ''}
                onChange={handleItemChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors["accountNumber"] ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors["accountNumber"] && (
                <span className="text-red-500 text-sm">{errors["accountNumber"]}</span>
              )}
            </div>
          )}

          {item?.type === secretTypesConstants.BankAccounts && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number CCI</label>
              <input
                type="text"
                name="accountNumberCCI"
                value={item?.accountNumberCCI || ''}
                onChange={handleItemChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors["accountNumberCCI"] ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors["accountNumberCCI"] && (
                <span className="text-red-500 text-sm">{errors["accountNumberCCI"]}</span>
              )}
            </div>
          )}

          {item?.type === secretTypesConstants.BankAccounts && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">International Information Bank</label>
                <input
                  type="text"
                  name="internationalInformationBank"
                  value={item?.internationalInformationBank || ''}
                  onChange={handleItemChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors["internationalInformationBank"] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors["internationalInformationBank"] && (
                  <span className="text-red-500 text-sm">{errors["internationalInformationBank"]}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">International Information Address</label>
                <input
                  type="text"
                  name="internationalInformationAddress"
                  value={item?.internationalInformationAddress || ''}
                  onChange={handleItemChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors["internationalInformationAddress"] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors["internationalInformationAddress"] && (
                  <span className="text-red-500 text-sm">{errors["internationalInformationAddress"]}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">International Information Swift</label>
                <input
                  type="text"
                  name="internationalInformationSwift"
                  value={item?.internationalInformationSwift || ''}
                  onChange={handleItemChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors["internationalInformationSwift"] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors["internationalInformationSwift"] && (
                  <span className="text-red-500 text-sm">{errors["internationalInformationSwift"]}</span>
                )}
              </div>
            </>
          )}

          {item?.type === secretTypesConstants.SunatCredentials && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sunat RUC</label>
              <input
                type="text"
                name="sunatInformationRUC"
                value={item?.sunatInformationRUC || ''}
                onChange={handleItemChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors["sunatInformationRUC"] ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors["sunatInformationRUC"] && (
                <span className="text-red-500 text-sm">{errors["sunatInformationRUC"]}</span>
              )}
            </div>
          )}

          {item?.type === secretTypesConstants.CreditCards && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Type</label>
              <CardTypeSelect
                label="CardType"
                required
                selectId="cardType"
                defaultSelectedItem={item?.cardType}
                setSelectedItem={(selected) => {
                  secretFormManager.setItemField('cardType', selected?.value);
                }}
                showLabel={false}
              />
              {errors["cardType"] && (
                <span className="text-red-500 text-sm">{errors["cardType"]}</span>
              )}
            </div>
          )}

          {(item?.type !== secretTypesConstants.BankAccounts &&
            item?.type !== secretTypesConstants.BankDetractionAccounts) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{labelTextSecretKey(item?.type)}</label>
              <input
                type="text"
                name="secretKey"
                value={item?.secretKey || ''}
                onChange={handleItemChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors["secretKey"] ? 'border-red-500' : 'border-gray-300'
                }`}
                required={item?.type === secretTypesConstants.SunatCredentials || item?.type === secretTypesConstants.DatabaseCredentials}
              />
              {errors["secretKey"] && (
                <span className="text-red-500 text-sm">{errors["secretKey"]}</span>
              )}
            </div>
          )}

          {(item?.type !== secretTypesConstants.BankAccounts &&
            item?.type !== secretTypesConstants.BankDetractionAccounts) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{labelTextSecretValue(item?.type)}</label>
              <input
                type="text"
                name="secretValue"
                value={item?.secretValue || ''}
                onChange={handleItemChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors["secretValue"] ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors["secretValue"] && (
                <span className="text-red-500 text-sm">{errors["secretValue"]}</span>
              )}
            </div>
          )}

          {item?.type === secretTypesConstants.CreditCards && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card Expiration</label>
                <input
                  type="text"
                  name="cardExpiration"
                  value={item?.cardExpiration || ''}
                  onChange={handleItemChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors["cardExpiration"] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors["cardExpiration"] && (
                  <span className="text-red-500 text-sm">{errors["cardExpiration"]}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Name</label>
                <input
                  type="text"
                  name="userName"
                  value={item?.userName || ''}
                  onChange={handleItemChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors["userName"] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors["userName"] && (
                  <span className="text-red-500 text-sm">{errors["userName"]}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Last Name</label>
                <input
                  type="text"
                  name="userLastName"
                  value={item?.userLastName || ''}
                  onChange={handleItemChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors["userLastName"] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors["userLastName"] && (
                  <span className="text-red-500 text-sm">{errors["userLastName"]}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
                <input
                  type="email"
                  name="userEmail"
                  value={item?.userEmail || ''}
                  onChange={handleItemChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors["userEmail"] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors["userEmail"] && (
                  <span className="text-red-500 text-sm">{errors["userEmail"]}</span>
                )}
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={item?.notes || ''}
              onChange={handleItemChange}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                errors["notes"] ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors["notes"] && (
              <span className="text-red-500 text-sm">{errors["notes"]}</span>
            )}
          </div>
        </form>
      </div>

      {/* Fixed Actions */}
      <div className="p-6 pt-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-3">
          <button
            onClick={closeForm}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center"
          >
            {submitting ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

SecretFormSingleton.displayName = 'SecretFormSingleton';

export default SecretFormSingleton;
